// AGA (Avaliação Geriátrica Ampla) form schema — 3 sections for MVP:
//  1. Identification & anamnesis (patient-fillable)
//  2. Lifestyle, meds, vaccines (patient-fillable)
//  3. Functional/cognitive scales + physical exam (doctor-fillable)
// Plus dedicated Dehospitalization Checklist section (C-R-A-F-A-E-P-D-S)

export const NPI_Q_DOMAINS = [
    { key: "delirios", label: "Delírios", hint: "Acredita em coisas que não são verdadeiras (roubo, traição, perseguição)?" },
    { key: "alucinacoes", label: "Alucinações", hint: "Vê, ouve ou sente coisas que não estão presentes?" },
    { key: "agitacao", label: "Agitação / Agressividade", hint: "Resistente a cuidados, agressivo verbal ou fisicamente?" },
    { key: "depressao", label: "Depressão / Disforia", hint: "Triste, chora com facilidade, sente-se inútil?" },
    { key: "ansiedade", label: "Ansiedade", hint: "Preocupação excessiva, tensão sem motivo aparente?" },
    { key: "euforia", label: "Euforia / Elação", hint: "Excessivamente alegre ou humor inadequado?" },
    { key: "apatia", label: "Apatia / Indiferença", hint: "Perdeu interesse por atividades e pessoas?" },
    { key: "desinibicao", label: "Desinibição", hint: "Age sem controle social, faz comentários inadequados?" },
    { key: "irritabilidade", label: "Irritabilidade / Labilidade", hint: "Irrita-se com facilidade, muda de humor bruscamente?" },
    { key: "motor_aberrante", label: "Comportamento Motor Aberrante", hint: "Anda sem rumo, mexe em objetos repetidamente?" },
    { key: "sono", label: "Sono / Comportamento Noturno", hint: "Insônia, acorda à noite, troca o dia pela noite?" },
    { key: "apetite", label: "Apetite / Alterações Alimentares", hint: "Mudança de hábitos alimentares, perda ou ganho de peso?" },
];

export const NPI_Q_SEVERITY = [
    { value: 1, label: "1 · Leve" },
    { value: 2, label: "2 · Moderado" },
    { value: 3, label: "3 · Grave" },
];

export const NPI_Q_STRESS = [
    { value: 0, label: "0 · Nenhum" },
    { value: 1, label: "1 · Mínimo" },
    { value: 2, label: "2 · Leve" },
    { value: 3, label: "3 · Moderado" },
    { value: 4, label: "4 · Severo" },
    { value: 5, label: "5 · Extremo" },
];

export function computeNpiTotals(data) {
    let gravidade = 0, estresse = 0, ativos = 0;
    for (const d of NPI_Q_DOMAINS) {
        const item = (data || {})[d.key];
        if (item && item.present) {
            ativos++;
            gravidade += Number(item.gravidade || 0) || 0;
            estresse += Number(item.estresse || 0) || 0;
        }
    }
    return { gravidade, estresse, ativos };
}

export const CONSULTATION_TYPES = [
    { value: "ambulatorial", label: "Consulta Geriátrica Ambulatorial" },
    { value: "retorno", label: "Consulta de Retorno" },
    { value: "domiciliar", label: "Acompanhamento Domiciliar" },
    { value: "telemedicina_canabis", label: "Telemedicina — Canabis Medicinal" },
];

export const PAYMENT_MODELS = [
    { value: "bundle_payment", label: "Bundle Payment (pacote)" },
    { value: "capitation", label: "Capitation (per capita)" },
];

export const STATUS_LABELS = {
    scheduled: "Agendada",
    intake_pending: "Aguardando preenchimento",
    intake_completed: "Pré-consulta preenchida",
    in_progress: "Em atendimento",
    completed: "Concluída",
    cancelled: "Cancelada",
};

export const AGA_SECTIONS = [
    {
        id: "identificacao",
        title: "Identificação do Paciente",
        subtitle: "Dados básicos (pode ser preenchido pelo paciente ou cuidador)",
        forPatient: true,
        fields: [
            { key: "motivo_consulta", label: "Motivo da consulta / demanda principal / expectativa", type: "textarea", rows: 3 },
            { key: "queixa_principal_hda", label: "Queixa principal e história da doença atual (HDA)", type: "textarea", rows: 5 },
        ],
    },
    {
        id: "antecedentes",
        title: "Antecedentes Pessoais",
        subtitle: "Doenças, cirurgias, alergias e internações prévias",
        forPatient: true,
        fields: [
            { key: "antecedentes_pessoais_patologicos", label: "Doenças crônicas (HAS, DM, ICC, DPOC, demências, etc.)", type: "textarea", rows: 3, placeholder: "Ex.: HAS desde 2010 (bem controlada), DM2 desde 2015..." },
            { key: "antecedentes_cirurgicos", label: "Cirurgias prévias relevantes", type: "textarea", rows: 2 },
            { key: "alergias", label: "Alergias medicamentosas / alimentares", type: "textarea", rows: 2 },
            { key: "internacoes", label: "Internações relevantes (motivo, ano, desfecho)", type: "textarea", rows: 2 },
        ],
    },
    {
        id: "social_estilo_vida",
        title: "História de Vida e Estilo de Vida",
        subtitle: "Hábitos, sono, alimentação, contexto social",
        forPatient: true,
        fields: [
            { key: "tabagismo", label: "Tabagismo", type: "select", options: [
                { value: "nunca", label: "Nunca fumou" },
                { value: "atual", label: "Fumante atual" },
                { value: "ex", label: "Ex-tabagista" },
            ]},
            { key: "etilismo", label: "Etilismo", type: "select", options: [
                { value: "nao", label: "Não" },
                { value: "social", label: "Social" },
                { value: "regular", label: "Regular" },
            ]},
            { key: "atividade_fisica", label: "Atividade física", type: "select", options: [
                { value: "sedentario", label: "Sedentário" },
                { value: "regular", label: "Regular" },
            ]},
            { key: "sono", label: "Sono", type: "select", options: [
                { value: "adequado", label: "Adequado" },
                { value: "insonia", label: "Insônia" },
                { value: "sonolencia", label: "Sonolência excessiva" },
            ]},
            { key: "alimentacao_autonomia", label: "Alimentação, autonomia no domicílio e observações sociais", type: "textarea", rows: 3 },
        ],
    },
    {
        id: "vacinacao",
        title: "Vacinação e Prevenção",
        subtitle: "Vacinas em dia e pendências",
        forPatient: true,
        fields: [
            { key: "vacinas", label: "Vacinas registradas", type: "checkboxes", options: [
                "Influenza", "Pneumocócica", "Herpes-zóster", "dT/dTpa", "COVID-19", "Hepatite B",
            ]},
            { key: "vacinas_pendencias", label: "Pendências vacinais / contraindicações / observações", type: "textarea", rows: 2 },
        ],
    },
    {
        id: "medicamentos",
        title: "Medicamentos — Reconciliação Medicamentosa",
        subtitle: "TODOS os medicamentos em uso (incluindo fitoterápicos e suplementos)",
        forPatient: true,
        fields: [
            { key: "medications", label: "Medicamentos em uso", type: "meds_table" },
            { key: "polifarmacia", label: "Polifarmácia (5 ou mais medicamentos)", type: "boolean" },
        ],
    },
    {
        id: "funcionalidade",
        title: "Funcionalidade e Mobilidade",
        subtitle: "Escalas Katz, Lawton-Brody, quedas e TUG",
        forPatient: false,
        fields: [
            { key: "katz_score", label: "Escore de Katz (0-6, quanto maior mais independente)", type: "number", min: 0, max: 6 },
            { key: "katz_obs", label: "Observações Katz (banho, vestir, higiene, alimentação, mobilidade, continência)", type: "textarea", rows: 2 },
            { key: "lawton_score", label: "Escore de Lawton-Brody (0-27)", type: "number", min: 0, max: 27 },
            { key: "lawton_obs", label: "Observações Lawton (telefone, compras, refeições, casa, transporte, finanças, medicação)", type: "textarea", rows: 2 },
            { key: "quedas_12m", label: "Nº de quedas nos últimos 12 meses", type: "number", min: 0 },
            { key: "medo_cair", label: "Medo de cair?", type: "boolean" },
            { key: "tug_seg", label: "TUG (segundos)", type: "number" },
            { key: "velocidade_marcha", label: "Velocidade de marcha (m/s)", type: "number", step: 0.1 },
            { key: "morse_score", label: "Escala de Morse (risco de quedas)", type: "number" },
        ],
    },
    {
        id: "cognicao_humor_nutricao",
        title: "Cognição, Humor, Nutrição e Fragilidade",
        subtitle: "Escalas estruturadas: MEEM, GDS-15, PHQ-9, MAN-SF, Fried, CFS",
        forPatient: false,
        fields: [
            { key: "meem", label: "MEEM (Mini-Exame do Estado Mental)", type: "number", min: 0, max: 30 },
            { key: "teste_relogio", label: "Teste do Relógio (0-10)", type: "number", min: 0, max: 10 },
            { key: "teste_relogio_foto", label: "Foto do Teste do Relógio", type: "clock_drawing_photo" },
            { key: "fluencia_verbal", label: "Fluência verbal (palavras/min)", type: "number" },
            { key: "gds15", label: "GDS-15", type: "number", min: 0, max: 15 },
            { key: "phq9", label: "PHQ-9", type: "number", min: 0, max: 27 },
            { key: "peso_kg", label: "Peso (kg)", type: "number", step: 0.1 },
            { key: "altura_cm", label: "Altura (cm)", type: "number" },
            { key: "panturrilha_cm", label: "Panturrilha (cm)", type: "number", step: 0.1 },
            { key: "man_score", label: "MAN — Mini Nutritional Assessment", type: "number" },
            { key: "fried", label: "Fried (fenótipo de fragilidade 0-5)", type: "number", min: 0, max: 5 },
            { key: "cfs", label: "CFS — Clinical Frailty Scale (1-9)", type: "number", min: 1, max: 9 },
            { key: "zarit", label: "Zarit — Sobrecarga do cuidador", type: "number" },
            { key: "interpretacao_aga", label: "Interpretação clínica da AGA", type: "textarea", rows: 4 },
        ],
    },
    {
        id: "npi_q",
        title: "NPI-Q — Inventário Neuropsiquiátrico",
        subtitle: "12 domínios comportamentais. Marque presença, gravidade (1-3) e estresse do cuidador (0-5).",
        forPatient: false,
        fields: [
            { key: "npi_data", label: "NPI-Q", type: "npi_q_table" },
            { key: "npi_observacoes", label: "Observações clínicas do NPI-Q", type: "textarea", rows: 3 },
        ],
    },
    {
        id: "exame_fisico",
        title: "Exame Físico",
        subtitle: "Sinais vitais e achados",
        forPatient: false,
        fields: [
            { key: "pa_sentado", label: "PA sentado (ex.: 130/80)", type: "text" },
            { key: "pa_ortostatica", label: "PA ortostática", type: "text" },
            { key: "fc", label: "FC (bpm)", type: "number" },
            { key: "fr", label: "FR (irpm)", type: "number" },
            { key: "spo2", label: "SpO₂ (%)", type: "number" },
            { key: "temp", label: "Temperatura (°C)", type: "number", step: 0.1 },
            { key: "cardio_respiratorio", label: "Exame cardiovascular e respiratório", type: "textarea", rows: 3 },
            { key: "neuro_musc_pele", label: "Neurológico, musculoesquelético, pele, demais achados", type: "textarea", rows: 3 },
        ],
    },
    {
        id: "impressao_conduta",
        title: "Impressão Diagnóstica & Conduta",
        subtitle: "Diagnósticos, plano terapêutico e orientações",
        forPatient: false,
        fields: [
            { key: "impressao_diagnostica", label: "Impressão diagnóstica principal", type: "textarea", rows: 4 },
            { key: "diagnosticos_cid", label: "Diagnósticos secundários / CID-10", type: "textarea", rows: 3 },
            { key: "conduta_imediata", label: "Conduta / Plano terapêutico", type: "textarea", rows: 5 },
            { key: "exames_solicitados", label: "Exames complementares solicitados", type: "textarea", rows: 3 },
            { key: "encaminhamentos", label: "Encaminhamentos (especialidades, terapias, serviços)", type: "textarea", rows: 3 },
            { key: "orientacoes", label: "Orientações ao paciente e cuidador", type: "textarea", rows: 3 },
            { key: "retorno_previsto", label: "Retorno previsto (data ou intervalo)", type: "text" },
        ],
    },
];

export const DEHOSP_CHECKLIST = [
    {
        id: "C",
        title: "C — Contato Principal",
        items: [
            "Nome completo do responsável/cuidador principal",
            "Telefone celular e fixo",
            "Grau de parentesco/vínculo",
            "Endereço completo",
            "Contato secundário (backup)",
        ],
    },
    {
        id: "R",
        title: "R — Resumo de Alta",
        items: [
            "História clínica completa da internação",
            "Motivo da internação e diagnósticos principais",
            "Complicações (infecções, delirium, quedas)",
            "Procedimentos realizados (IOT, TQT, gastrostomia, diálise)",
            "Evolução clínica diária",
            "Relatórios: enfermagem, fisio, fono, nutrição, TO, serviço social",
        ],
    },
    {
        id: "A1",
        title: "A — Antecedentes",
        items: [
            "Doenças crônicas prévias",
            "Internações anteriores",
            "Cirurgias prévias relevantes",
            "Alergias medicamentosas",
            "Estado funcional e cognitivo pré-mórbido",
        ],
    },
    {
        id: "F",
        title: "F — Funcionalidade",
        items: [
            "Nível de dependência atual (AVDs)",
            "Capacidade de deambulação",
            "Dispositivos de marcha necessários",
            "Risco de quedas avaliado",
            "Plano de reabilitação domiciliar",
        ],
    },
    {
        id: "A2",
        title: "A — Alimentação",
        items: [
            "Via de alimentação (oral, SNE, gastrostomia, mista)",
            "Tipo de dieta e consistências",
            "Avaliação de disfagia",
            "Treinamento do cuidador",
            "Risco de broncoaspiração",
        ],
    },
    {
        id: "E",
        title: "E — Eliminações",
        items: [
            "Continência urinária",
            "Continência fecal / ostomias",
            "Uso de fraldas / frequência de troca",
            "Padrão intestinal / laxativos",
            "Cateterismo vesical",
        ],
    },
    {
        id: "P",
        title: "P — Pele e Dispositivos",
        items: [
            "Lesões por pressão (local, estágio)",
            "Feridas cirúrgicas / vasculares",
            "Protocolo de curativos",
            "Dispositivos em uso (O₂, TQT, SNE, GTT, SVD, PICC, drenos)",
        ],
    },
    {
        id: "D",
        title: "D — Drogas / Medicamentos",
        items: [
            "Reconciliação medicamentosa completa",
            "Psicotrópicos (revisão e desprescrição)",
            "Insulina — treinamento",
            "Opioides — indicação e ajuste",
            "Anti-hipertensivos — risco de queda",
            "Anticoagulantes — monitoramento",
        ],
    },
    {
        id: "S",
        title: "S — Seguimento e Pendências",
        items: [
            "Exames laboratoriais / imagem pendentes",
            "Retorno agendado (7-15 dias)",
            "Consultas com especialistas",
            "Visitas domiciliares programadas",
            "Retirada de dispositivos (PICC, pontos)",
        ],
    },
];


// ---------- Anamnese em Medicina Endocanabinoide ----------
// Usado exclusivamente na modalidade "telemedicina_canabis"
export const CANABIS_SECTIONS = [
    {
        id: "identificacao",
        title: "1. Identificação do Paciente",
        subtitle: "Dados básicos e queixa principal (pode ser preenchido pelo paciente)",
        forPatient: true,
        fields: [
            { key: "profissao", label: "Profissão", type: "text", placeholder: "Importante para avaliar impacto de sonolência e outros efeitos colaterais" },
            { key: "queixa_principal", label: "Queixa principal (QP) — o que mais incomoda hoje?", type: "textarea", rows: 3 },
        ],
    },
    {
        id: "hda_sintomas_alvo",
        title: "2. Histórico da Doença Atual e Sintomas-Alvo",
        subtitle: "Sintomas principais e tratamentos prévios",
        forPatient: true,
        fields: [
            { key: "sintomas_principais", label: "Sintomas principais", type: "checkboxes", options: [
                "Dor crônica", "Ansiedade", "Insônia", "Depressão", "Tremores",
                "Espasticidade", "Náuseas", "Perda de apetite", "Epilepsia/convulsões", "Neuropatia",
                "Migrânea/cefaleia", "TDAH", "TEPT", "Autismo/comportamento", "Outros",
            ]},
            { key: "sintomas_outros_especificar", label: "Se marcou 'Outros', especifique", type: "text" },
            { key: "intensidade_atual", label: "Intensidade atual da queixa principal (escala 0-10)", type: "number", min: 0, max: 10 },
            { key: "fatores_melhora", label: "Fatores de melhora", type: "textarea", rows: 2, placeholder: "Repouso, calor, medicação, atividade..." },
            { key: "fatores_piora", label: "Fatores de piora", type: "textarea", rows: 2, placeholder: "Frio, estresse, esforço, alimentos..." },
            { key: "tratamentos_anteriores", label: "Tratamentos anteriores tentados (alopáticos, terapias, cirurgias) — e por que falharam ou foram descontinuados", type: "textarea", rows: 4 },
        ],
    },
    {
        id: "historico_canabis",
        title: "3. Histórico de Uso de Canabinoides",
        subtitle: "Uso prévio ou atual de Cannabis",
        forPatient: true,
        fields: [
            { key: "ja_usou_cannabis", label: "Já fez uso de Cannabis?", type: "boolean" },
            { key: "via_tipo", label: "Se sim, via/tipo utilizado", type: "select", options: [
                { value: "medicinal_oleo", label: "Medicinal — Óleo/Gummies (sublingual/oral)" },
                { value: "medicinal_inalado", label: "Medicinal — Inalado (vaporização)" },
                { value: "recreativo_inalado", label: "Adulto/Recreativo — Inalado" },
                { value: "topico", label: "Tópico" },
                { value: "outros", label: "Outros" },
            ]},
            { key: "experiencia_beneficios", label: "Experiência — benefícios percebidos", type: "textarea", rows: 3 },
            { key: "efeitos_adversos", label: "Efeitos adversos observados (ansiedade, taquicardia, sonolência, boca seca, etc.)", type: "textarea", rows: 2 },
            { key: "uso_atual", label: "Uso atual (auto-medicação) — produto, concentração, dose, frequência", type: "textarea", rows: 2, placeholder: "Ex.: Óleo full spectrum 20mg/mL, 5 gotas 2x/dia..." },
        ],
    },
    {
        id: "triagem_contraindicacoes",
        title: "4. Histórico Médico e Triagem de Contraindicações",
        subtitle: "⚠️ Avaliar contraindicações relativas ou absolutas a THC/CBD",
        forPatient: true,
        fields: [
            { key: "psiquiatrico_pessoal", label: "Histórico psiquiátrico pessoal (psicose, esquizofrenia, transtorno bipolar, ideação suicida)", type: "textarea", rows: 2 },
            { key: "psiquiatrico_familiar", label: "Histórico psiquiátrico familiar", type: "textarea", rows: 2 },
            { key: "cardiovascular", label: "Histórico cardiovascular (arritmias severas, infarto recente, ICC)", type: "textarea", rows: 2 },
            { key: "hepatico_renal", label: "Condições hepáticas ou renais (metabolismo comprometido)", type: "textarea", rows: 2 },
            { key: "gravidez_lactacao", label: "Possibilidade de gravidez ou lactação", type: "boolean" },
            { key: "outras_contraindicacoes", label: "Outras contraindicações relevantes", type: "textarea", rows: 2 },
        ],
    },
    {
        id: "revisao_sistemas",
        title: "5. Revisão de Sistemas e Estilo de Vida",
        subtitle: "Sono, humor, apetite, atividade — todos interagem com o SEC",
        forPatient: true,
        fields: [
            { key: "sono_latencia", label: "Padrão de sono — latência (min para adormecer)", type: "number" },
            { key: "sono_despertares", label: "Nº de despertares noturnos", type: "number" },
            { key: "sono_qualidade", label: "Qualidade subjetiva do sono", type: "select", options: [
                { value: "otima", label: "Ótima" },
                { value: "boa", label: "Boa" },
                { value: "regular", label: "Regular" },
                { value: "ruim", label: "Ruim" },
                { value: "pessima", label: "Péssima" },
            ]},
            { key: "humor_energia", label: "Humor e energia ao longo do dia (ansiedade, depressão, fadiga)", type: "textarea", rows: 3 },
            { key: "apetite_gi", label: "Apetite e trato gastrointestinal (náuseas, dores, regularidade)", type: "textarea", rows: 2 },
            { key: "atividade_dieta", label: "Atividade física e dieta", type: "textarea", rows: 2 },
        ],
    },
    {
        id: "historico_farmacologico",
        title: "6. Histórico Farmacológico (Interações Medicamentosas)",
        subtitle: "⚠️ CBD/THC metabolizados via CYP3A4 e CYP2C19 — atenção a benzodiazepínicos, antidepressivos, anticoagulantes, anticonvulsivantes e opioides",
        forPatient: true,
        fields: [
            { key: "medications", label: "Medicamentos em uso contínuo", type: "meds_table" },
            { key: "atencao_interacoes", label: "Observações sobre potenciais interações", type: "textarea", rows: 3, placeholder: "Ex.: Uso de warfarina — necessidade de monitorar INR..." },
        ],
    },
    {
        id: "proposta_terapeutica",
        title: "🛠️ Proposta Terapêutica — Plano de Manejo",
        subtitle: "Estratégia 'Start Low, Go Slow' — apenas para preenchimento pelo médico",
        forPatient: false,
        fields: [
            { key: "raciocinio_clinico", label: "Raciocínio clínico — por que Rico em CBD, Rico em THC ou Balanced/Full Spectrum?", type: "textarea", rows: 4 },
            { key: "perfil_produto", label: "Perfil do produto escolhido", type: "select", options: [
                { value: "rico_cbd", label: "Rico em CBD (isolado ou broad spectrum)" },
                { value: "rico_thc", label: "Rico em THC" },
                { value: "balanced", label: "Balanced 1:1 (THC:CBD)" },
                { value: "full_spectrum", label: "Full Spectrum" },
                { value: "outro", label: "Outro" },
            ]},
            { key: "produto_nome", label: "Produto escolhido — nome comercial e concentração (mg/mL)", type: "text", placeholder: "Ex.: Óleo Full Spectrum 20mg/mL CBD + 1mg/mL THC" },
            { key: "dose_inicial", label: "Dose inicial (gotas ou mg, dividida ao longo do dia)", type: "textarea", rows: 2, placeholder: "Ex.: 1 gota (0,66mg CBD) 2x/dia — manhã e noite" },
            { key: "estrategia_titulacao", label: "Estratégia de titulação", type: "textarea", rows: 3, placeholder: "Ex.: Aumentar 1 gota a cada 3-5 dias até dose alvo de 25mg CBD/dia ou efeito clínico" },
            { key: "efeitos_monitorar", label: "Efeitos e sinais a monitorar", type: "textarea", rows: 2, placeholder: "Sonolência, boca seca, tontura, taquicardia, ansiedade paradoxal..." },
            { key: "proxima_consulta", label: "Próxima consulta / follow-up", type: "text", placeholder: "Ex.: 15-30 dias para avaliar eficácia e colaterais" },
            { key: "orientacoes_paciente", label: "Orientações ao paciente", type: "textarea", rows: 3 },
        ],
    },
];

// Helper: get sections by consultation type
export function getFormSectionsForType(consultationType) {
    if (consultationType === "telemedicina_canabis") return CANABIS_SECTIONS;
    return AGA_SECTIONS;
}
